import React from 'react';
import { Edit3 } from 'lucide-react';
import { ISSUE_OPTIONS } from './PayrollFeedbackModal';

interface PayrollFeedbackBannerProps {
  submittedBy: string;
  submittedAt: string;
  issueKeys: string[];
  comment?: string | null;
}

const ISSUE_LABEL_BY_KEY: Record<string, string> = ISSUE_OPTIONS.reduce(
  (acc, opt) => {
    acc[opt.key] = opt.label;
    return acc;
  },
  {} as Record<string, string>
);

const formatTimestamp = (iso: string): string => {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return iso;
  }
  return parsed.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const PayrollFeedbackBanner: React.FC<PayrollFeedbackBannerProps> = ({
  submittedBy,
  submittedAt,
  issueKeys,
  comment,
}) => {
  const formattedTimestamp = formatTimestamp(submittedAt);
  const trimmedComment = comment?.trim();

  return (
    <div className="p-4 bg-cyan-50 border-2 border-cyan-300 rounded-xl shadow-md">
      <div className="flex items-start space-x-3">
        <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
          <Edit3 size={20} className="text-cyan-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <span className="font-bold text-cyan-900 text-lg">
              Payroll Feedback Submitted
            </span>
          </div>
          <p className="text-cyan-800 text-sm mb-2">
            A user reported the following while processing this payroll. This feedback
            helps measure and improve the Payroll Agent.
          </p>

          <div className="bg-cyan-100 rounded-lg p-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-cyan-700 font-medium">Submitted by:</span>{' '}
                <span className="text-cyan-900 break-all">{submittedBy}</span>
              </div>
              <div>
                <span className="text-cyan-700 font-medium">Submitted at:</span>{' '}
                <span className="text-cyan-900">{formattedTimestamp}</span>
              </div>

              {issueKeys.length > 0 && (
                <div className="col-span-2">
                  <span className="text-cyan-700 font-medium">Issues reported:</span>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {issueKeys.map((key) => (
                      <span
                        key={key}
                        className="bg-cyan-200 text-cyan-900 text-xs font-medium px-2 py-0.5 rounded-full"
                      >
                        {ISSUE_LABEL_BY_KEY[key] ?? key}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {trimmedComment && (
                <div className="col-span-2">
                  <span className="text-cyan-700 font-medium">Comment:</span>{' '}
                  <span className="text-cyan-900 whitespace-pre-wrap break-words">
                    {trimmedComment}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayrollFeedbackBanner;
